import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../../shared/ThemedChartTooltip";
import { fmtNum } from "../utils";

// ─── ThroughputChart ────────────────────────────────────────────────────────────
// Two-line chart: completed vs created tasks, by week. Spec says "the only number
// that predicts trouble — intake above throughput for three weeks means something
// breaks, visibly, before it does." Recharts replaces the hand-rolled SVG
// (tracker §"Replace plain SVG charts with recharts").
//
// Props: { data } where data is array of { week, completed, created }.
//   • Green (#22c55e) line = completed (throughput)
//   • Blue  (#32B1FF)  line = created (intake)
//   • Weeks where created > completed get an amber (#f59e0b) vertical
//     ReferenceLine — intake above throughput = trouble.
//   • null/undefined/[] data → "No throughput data yet".
const COLORS = { completed: "#22c55e", created: "#32B1FF", amber: "#f59e0b" };

// Abbreviate a week value for the x-axis.
const fmtWeek = (w) => {
  if (!w) return "";
  const s = String(w);
  const d = new Date(s + "T00:00:00");
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return s.slice(0, 8); // fall back to a truncated label
};

const ThroughputChart = ({ data, openDrill }) => {
  // Empty or missing data — spec: show "No throughput data yet" when empty/missing
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Throughput vs Intake</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Completed against created, by week.</p>
        <div className="mt-4 h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">No throughput data yet</p>
        </div>
      </div>
    );
  }

  // Sort ascending by week so the earliest date is leftmost (tracker: dates run
  // earliest → most recent left to right). Aggregate duplicate week entries
  // (summing completed + created) so each week is unique — prevents Recharts
  // duplicate-key warnings on the amber ReferenceLine keys (tracker: "Fix
  // Recharts duplicate key warning", key `amber-2026-08-17`) and ensures the
  // XAxis dataKey="week" has exactly one value per data point.
  const sortedData = Object.values(
    [...data].reduce((acc, r) => {
      const week = r.week;
      const existing = acc[week];
      if (existing) {
        existing.completed = (Number(existing.completed) || 0) + (Number(r.completed) || 0);
        existing.created = (Number(existing.created) || 0) + (Number(r.created) || 0);
      } else {
        acc[week] = {
          ...r,
          completed: Number(r.completed) || 0,
          created: Number(r.created) || 0,
        };
      }
      return acc;
    }, {})
  ).sort((a, b) => new Date(a.week) - new Date(b.week));

  // Weeks where intake (created) exceeds throughput (completed) → amber marker.
  const amberWeeks = sortedData
    .filter((r) => (Number(r.created) || 0) > (Number(r.completed) || 0))
    .map((r) => r.week);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      {/* Header + legend */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Throughput vs Intake</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Completed against created, by week.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS.completed }} />
            Completed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS.created }} />
            Created
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={350}>
          <LineChart
            data={sortedData}
            margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
            onClick={(d) => {
              // Recharts passes the active entry; extract the underlying row
              // (activePayload for chart-level clicks, payload for series-level).
              const row = d?.activePayload?.[0]?.payload ?? d?.payload ?? d;
              if (row && row.week != null) openDrill?.("throughput_week", row.week);
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 12, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={{ stroke: "#374151" }}
              tickFormatter={fmtWeek}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={40}
              allowDecimals={false}
            />
            <ThemedChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload;
                const intake = Number(row.created) || 0;
                const throughput = Number(row.completed) || 0;
                const trouble = intake > throughput;
                return (
                  <ThemedTooltipBox title={fmtWeek(label)}>
                    <div style={{ color: COLORS.created }}>{fmtNum(intake)} created</div>
                    <div style={{ color: COLORS.completed }}>{fmtNum(throughput)} completed</div>
                    {trouble && (
                      <div style={{ color: COLORS.amber, marginTop: 4 }}>Intake above throughput</div>
                    )}
                  </ThemedTooltipBox>
                );
              }}
            />
            {/* Amber vertical markers for weeks where created > completed */}
            {amberWeeks.map((w) => (
              <ReferenceLine
                key={`amber-${w}`}
                x={w}
                stroke={COLORS.amber}
                strokeOpacity={0.45}
                strokeDasharray="4 4"
              />
            ))}
            <Line type="monotone" dataKey="completed" stroke={COLORS.completed} strokeWidth={2} dot={{ r: 2.5, fill: COLORS.completed }} activeDot={{ r: 4 }} isAnimationActive={false} name="Completed" />
            <Line type="monotone" dataKey="created" stroke={COLORS.created} strokeWidth={2} dot={{ r: 2.5, fill: COLORS.created }} activeDot={{ r: 4 }} isAnimationActive={false} name="Created" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {amberWeeks.length > 0 && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          Dashed amber = weeks where intake exceeded throughput.
        </p>
      )}
    </div>
  );
};

export default ThroughputChart;
